import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Mail, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-dark flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 bg-gradient-cta rounded-lg flex items-center justify-center shadow-neon">
              <span className="text-white font-bold text-xl">RV</span>
            </div>
          </Link>
          <h2 className="text-3xl font-bold mb-2">
            <span className="text-cream">Welcome </span>
            <span className="gradient-text-neon">Back</span>
          </h2>
          <p className="text-sky-blue">Sign in to access your staking dashboard</p>
        </div>

        {/* Form Card */}
        <div className="card-premium">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-cream mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-sky-blue" />
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-navy-dark border border-sky-blue/30 rounded-lg text-cream placeholder-sky-blue/50 focus:outline-none focus:border-neon-red transition-colors"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-cream mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-sky-blue" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-11 py-3 bg-navy-dark border border-sky-blue/30 rounded-lg text-cream placeholder-sky-blue/50 focus:outline-none focus:border-neon-red transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sky-blue hover:text-cream transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-sky-blue/30 bg-navy-dark text-neon-red focus:ring-neon-red"
                />
                <span className="ml-2 text-sm text-sky-blue">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-neon-red hover:text-accent-red transition-colors">
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-neon disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>

          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-sky-blue/20"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gradient-to-r from-navy-dark via-steel-blue to-navy-dark text-sky-blue">
                New to Residual Vault?
              </span>
            </div>
          </div>

          {/* Sign Up Link */}
          <Link
            to="/register"
            className="block w-full py-3 text-center border border-sky-blue/30 text-sky-blue rounded-lg hover:bg-sky-blue/10 transition-all font-medium"
          >
            Create an account
          </Link>
        </div>

        {/* Info Banner */}
        <div className="mt-6 card-glass flex items-start gap-3 p-4">
          <AlertCircle className="w-5 h-5 text-sky-blue flex-shrink-0 mt-0.5" />
          <div className="text-sm text-sky-blue">
            <strong className="text-cream">Secure Login:</strong> Your credentials are encrypted and we never store your private keys.
          </div>
        </div>

      </div>
    </div>
  );
}
